import { Injectable } from '@angular/core'
import {
  AnnotationsPeopleProfilesClient,
  CreatePeopleProfile,
  DEFAULT_PAGINATION_LIMIT,
  DetailedProfile,
  PaginatedPeopleProfiles,
  PeopleProfile,
  PeopleProfilesQueryParams,
  UpdatePeopleProfile,
} from '@ftr/annotations-contracts'
import { Uuid } from '@ftr/contracts/type/shared'
import { CourtSystemRegionCache } from '@ftr/data-court-system'
import { ApiResult, RemoteData } from '@ftr/foundation'
import { AnnotationsApiClientFactory } from '../../api-client'
import { AnnotationsService } from '../base'

@Injectable({
  providedIn: 'root',
})
export class PeopleProfilesService extends AnnotationsService {
  constructor(apiClientFactory: AnnotationsApiClientFactory, regionCache: CourtSystemRegionCache) {
    super(apiClientFactory, regionCache)
  }

  getPeopleProfileForUser(
    courtSystemId: Uuid,
    involvement: PeopleProfile['involvement'],
  ): ApiResult<PeopleProfile | null> {
    return this.runIfFeatureFlagEnabled(
      'feature-annotations-manage-people-settings',
      ApiResult.success<PeopleProfile | null>(null),
      this.performApiRequest<
        Awaited<ReturnType<AnnotationsPeopleProfilesClient['getPeopleProfileForUser']>>,
        PeopleProfile | null
      >({
        courtSystemId,
        requestFn: client => client.peopleProfiles.getPeopleProfileForUser({ query: { courtSystemId, involvement } }),
        responseMapFn: result => {
          if (result.status !== 200) {
            return RemoteData.success(null)
          }

          return RemoteData.success<PeopleProfile>(result.body)
        },
      }),
    )
  }

  getPeopleProfile(id: Uuid, courtSystemId: Uuid): ApiResult<PeopleProfile | null> {
    return this.runIfFeatureFlagEnabled(
      'feature-annotations-manage-people-settings',
      ApiResult.success<PeopleProfile | null>(null),
      this.performApiRequest<
        Awaited<ReturnType<AnnotationsPeopleProfilesClient['getPeopleProfile']>>,
        PeopleProfile | null
      >({
        courtSystemId,
        requestFn: client => client.peopleProfiles.getPeopleProfile({ params: { profileId: id } }),
        responseMapFn: result => {
          if (result.status !== 200) {
            return RemoteData.failure(new Error('Failed to fetch profile'))
          }

          return RemoteData.success<PeopleProfile>(result.body)
        },
      }),
    )
  }

  /**
   * @param options
   * @returns People profiles within a court system. Empty paginated result if feature flag is off
   */
  getPeopleProfiles(
    options: Partial<PeopleProfilesQueryParams> & Pick<PeopleProfilesQueryParams, 'courtSystemId'>,
  ): ApiResult<PaginatedPeopleProfiles> {
    return this.runIfFeatureFlagEnabled(
      'feature-annotations-manage-people-settings',
      ApiResult.success<PaginatedPeopleProfiles>({
        items: [],
        meta: { totalItems: 0, pageNumber: 1, pageSize: DEFAULT_PAGINATION_LIMIT },
      }),
      this.performApiRequest<
        Awaited<ReturnType<AnnotationsPeopleProfilesClient['listPeopleProfiles']>>,
        PaginatedPeopleProfiles
      >({
        courtSystemId: options.courtSystemId,
        requestFn: client =>
          client.peopleProfiles.listPeopleProfiles({
            query: { offset: 0, limit: DEFAULT_PAGINATION_LIMIT, ...options },
          }),
        responseMapFn: result => {
          if (result.status !== 200) {
            return RemoteData.failure(new Error('Failed to fetch profiles'))
          }

          return RemoteData.success<PaginatedPeopleProfiles>(result.body)
        },
      }),
    )
  }

  suggestPeopleProfiles(
    courtSystemId: Uuid,
    options: {
      searchTerm?: string
      caseId?: string
      caseReference?: string
      excludeHearingSectionId?: string
    },
  ): ApiResult<{
    items: Pick<PeopleProfile, 'id' | 'name' | 'personId' | 'email' | 'dateOfBirth'>[]
    meta: {
      suggestionSources: ('case' | 'search-term' | 'recent-assignments')[]
    }
  }> {
    return this.runIfFeatureFlagEnabled(
      'feature-annotations-manage-people-settings',
      ApiResult.success({ items: [], meta: { suggestionSources: [] } }),
      this.performApiRequest<
        Awaited<ReturnType<AnnotationsPeopleProfilesClient['suggestPeopleProfiles']>>,
        {
          items: Pick<PeopleProfile, 'id' | 'name' | 'personId' | 'email' | 'dateOfBirth'>[]
          meta: {
            suggestionSources: ('case' | 'search-term' | 'recent-assignments')[]
          }
        }
      >({
        courtSystemId,
        requestFn: client => client.peopleProfiles.suggestPeopleProfiles({ query: { courtSystemId, ...options } }),
        responseMapFn: result => {
          if (result.status !== 200) {
            return RemoteData.failure(new Error('Failed to suggest profiles'))
          }

          return RemoteData.success(result.body)
        },
      }),
    )
  }

  createProfile(body: CreatePeopleProfile): ApiResult<Partial<PeopleProfile>> {
    const { courtSystemId } = body
    return this.performApiRequest<
      Awaited<ReturnType<AnnotationsPeopleProfilesClient['createPeopleProfile']>>,
      PeopleProfile
    >({
      courtSystemId,
      requestFn: client =>
        client.peopleProfiles.createPeopleProfile({
          body,
        }),
      responseMapFn: result => {
        if (result.status !== 201) {
          return RemoteData.failure(new Error('Failed to create profiles'))
        }

        return RemoteData.success<PeopleProfile>(result.body)
      },
    })
  }

  updateProfile(profileId: Uuid, body: UpdatePeopleProfile, courtSystemId: Uuid): ApiResult<Partial<PeopleProfile>> {
    return this.performApiRequest<
      Awaited<ReturnType<AnnotationsPeopleProfilesClient['updatePeopleProfile']>>,
      PeopleProfile
    >({
      courtSystemId,
      requestFn: client => client.peopleProfiles.updatePeopleProfile({ body, params: { profileId } }),
      responseMapFn: result => {
        if (result.status !== 200) {
          return RemoteData.failure(new Error('Failed to update profile'))
        }

        return RemoteData.success<PeopleProfile>(result.body)
      },
    })
  }

  deleteProfile({ profileId, courtSystemId }: { profileId: Uuid; courtSystemId: Uuid }): ApiResult {
    return this.performApiRequest<Awaited<ReturnType<AnnotationsPeopleProfilesClient['deletePeopleProfile']>>, null>({
      courtSystemId,
      requestFn: client =>
        client.peopleProfiles.deletePeopleProfile({ params: { profileId }, query: { courtSystemId } }),
      responseMapFn: result => {
        if (result.status !== 204) {
          return RemoteData.failure(new Error('Failed to delete profile'))
        }

        return RemoteData.success<null>(null)
      },
    })
  }

  /**
   * Get detailed profile including associated cases, recordings, and live recording
   * @param profileId - The UUID of the profile to fetch
   * @param courtSystemId - The UUID of the court system
   * @returns Detailed profile with cases, recordings, and live recording data
   */
  getDetailedProfile(profileId: Uuid, courtSystemId: Uuid): ApiResult<DetailedProfile | null> {
    return this.runIfFeatureFlagEnabled(
      'feature-annotations-manage-people-settings',
      ApiResult.success<DetailedProfile | null>(null),
      this.performApiRequest<
        Awaited<ReturnType<AnnotationsPeopleProfilesClient['getDetailedProfile']>>,
        DetailedProfile | null
      >({
        courtSystemId,
        requestFn: client => client.peopleProfiles.getDetailedProfile({ params: { profileId } }),
        responseMapFn: result => {
          if (result.status === 404) {
            return RemoteData.success<DetailedProfile | null>(null)
          }

          if (result.status !== 200) {
            return RemoteData.failure(new Error('Failed to fetch detailed profile'))
          }

          return RemoteData.success<DetailedProfile>(result.body)
        },
      }),
    )
  }
}
